import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type { RepoSetupBackfillResponse, RepoSetupStepResult } from "@/lib/api";
import { cn } from "@/lib/utils";

type Outcome = RepoSetupStepResult["outcome"];

// What each backend step name means, in the admin's words (BOIE-267).
const STEP_LABELS: Record<string, string> = {
  repository: "Repository access",
  repo_settings: "Delete branch on merge, squash title",
  vulnerability_alerts: "Dependabot alerts",
  automated_security_fixes: "Dependabot security updates",
  supabase_branch_policy: "supabase environment, main only",
  supabase_branch_policy_extra: "supabase environment, other branches",
  supabase_repo_secrets: "Repo-level SUPABASE_* duplicates",
  supabase_repo_secrets_unmatched: "Repo-level SUPABASE_* secrets",
  pr_reviewer_key: "Codex reviewer key",
  pr_reviewer_model: "Codex reviewer model",
};

const OUTCOME_LABELS: Record<Outcome, string> = {
  ok: "ok",
  applied: "fixed",
  would_apply: "needs fix",
  failed: "failed",
  skipped: "skipped",
  manual: "needs you",
};

const OUTCOME_CLASSES: Record<Outcome, string> = {
  ok: "text-muted",
  applied: "text-green-600 dark:text-green-400",
  would_apply: "text-amber-600 dark:text-amber-400",
  failed: "text-destructive",
  skipped: "text-muted",
  manual: "text-orange-700 dark:text-orange-400",
};

// Only these get a line: a repo whose steps are all `ok` or `skipped` is fine.
const NOTEWORTHY: ReadonlySet<Outcome> = new Set(["applied", "would_apply", "failed", "manual"]);

interface RepoSetupResultProps {
  /** The last run that came back. */
  result: RepoSetupBackfillResponse;
  /**
   * Every repo a check since the last fresh check found a fix for and no apply
   * has reached yet: this batch's and the earlier batches'.
   */
  toFix: string[];
  /** Applies the fixes to exactly these repos (the ones the checks showed). */
  onApply: (repos: string[]) => void;
  /** Checks the repos after the last one the checks reached; absent once all were reached. */
  onCheckNext?: () => void;
  /** A check or fix is running. */
  busy: boolean;
  /** Why the run after this one failed, if it did. */
  error?: string | null;
}

/**
 * The outcome of the last repo setup check or fix run. A check writes nothing
 * to GitHub; it covers repos in batches, so a check that ran out of time offers
 * the next batch. Apply covers every repo the checks since the last fresh
 * check found a fix for, in every batch, and no others, after a confirmation
 * that names them all. Each repo lists only the steps that changed, need a
 * change, failed, or need a human.
 */
export function RepoSetupResult({
  result,
  toFix,
  onApply,
  onCheckNext,
  busy,
  error,
}: RepoSetupResultProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const noteworthy = result.repos
    .map((repo) => ({ ...repo, steps: repo.steps.filter((s) => NOTEWORTHY.has(s.outcome)) }))
    .filter((repo) => repo.steps.length > 0);
  // The repos waiting for a fix from an earlier batch: neither listed in this
  // result nor among the ones its time budget never reached.
  const shown = new Set([...result.repos.map((repo) => repo.repo), ...result.not_reached]);
  const waitingElsewhere = toFix.filter((repo) => !shown.has(repo));
  const plural = (n: number) => (n !== 1 ? "s" : "");

  const counts = [
    result.apply
      ? `${result.changed} fixed`
      : `${result.needs_change} need${result.needs_change === 1 ? "s" : ""} a fix`,
    `${result.failed} failed`,
    `${result.manual} need${result.manual === 1 ? "s" : ""} you`,
  ].join(", ");

  return (
    <div className="border-border bg-card mt-4 rounded-lg border p-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-medium">
          Repo setup {result.apply ? "fixed" : "checked"}: {result.checked} repo
          {plural(result.checked)} ({counts})
        </p>
        <div className="flex flex-wrap gap-2">
          {onCheckNext && (
            <Button size="sm" variant="outline" onClick={onCheckNext} disabled={busy}>
              Check next batch
            </Button>
          )}
          {toFix.length > 0 && (
            <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
              Apply fixes to {toFix.length} repo{plural(toFix.length)}
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-destructive mt-2">{error}</p>}

      {waitingElsewhere.length > 0 && (
        <p className="mt-2 text-amber-600 dark:text-amber-400">
          Also waiting for a fix, from an earlier batch: {waitingElsewhere.join(", ")}.
        </p>
      )}

      {!result.apply && (
        <p className="text-muted mt-1 text-xs">
          A check changes nothing on GitHub. It only updates each project&apos;s Codex reviewer
          warning here.
        </p>
      )}

      {result.not_reached.length > 0 && (
        <p className="mt-2 text-amber-600 dark:text-amber-400">
          {result.not_reached.length} repo{plural(result.not_reached.length)} not{" "}
          {result.apply ? "fixed" : "checked"} in time ({result.not_reached.join(", ")}).{" "}
          {result.apply
            ? "Apply fixes again to cover them."
            : "Check the next batch to cover them."}
        </p>
      )}

      {noteworthy.length === 0 ? (
        <p className="text-muted mt-2">Every repo in this batch is set up.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {noteworthy.map((repo) => (
            <li key={repo.project_id}>
              <p>
                {repo.project_name} <span className="text-muted text-xs">({repo.repo})</span>
              </p>
              <ul className="mt-1 space-y-0.5 pl-4">
                {repo.steps.map((step) => (
                  <li key={step.name} className="text-xs">
                    <span className="font-medium">{STEP_LABELS[step.name] ?? step.name}</span>{" "}
                    <span className={cn("font-medium", OUTCOME_CLASSES[step.outcome])}>
                      {OUTCOME_LABELS[step.outcome]}
                    </span>
                    {step.detail && <span className="text-muted">: {step.detail}</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply fixes to {toFix.length} repo{plural(toFix.length)}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This writes to GitHub on these {toFix.length} repo{plural(toFix.length)} only, from
              every batch checked: {toFix.join(", ")}. It changes their settings and may delete
              repository-level SUPABASE_* secrets the supabase environment also holds. Repos no
              check showed as needing a fix are left alone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onApply(toFix)}>Apply fixes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
